<template>
  <div>
    <b-row class="d-flex align-items-center">
      <b-col cols="8">
        <h1 class="font-weight-bolder mb-0">Update Banner Campaign</h1>
      </b-col>
      <b-col cols="4" class="text-right">
        <b-button
          size="md"
          variant="outline-secondary"
          class="mr-1"
          @click="handleUpdateBanner(true)"
        >
          <span class="text-lg text-nowrap" style="margin-left: 4px"
            >Save Draft</span
          >
        </b-button>
        <b-button
          size="md"
          variant="primary"
          :disabled="loading"
          @click="handleUpdateBanner(false)"
        >
          <span class="text-lg text-nowrap" style="margin-left: 4px"
            >Publish Campaign</span
          >
        </b-button>
      </b-col>
    </b-row>

    <validation-observer ref="formUpdate">
      <b-row class="mt-2">
        <b-col cols="12">
          <b-card>
            <h4>Campaign Details</h4>
            <b-form class="mt-1">
              <b-row>
                <!-- Campaign Name -->
                <b-col md="6">
                  <validation-provider
                    #default="{ errors }"
                    name="campaign name"
                    rules="required"
                  >
                    <b-form-group>
                      <p class="mb-0 font-weight-bold">Campaign Name*</p>
                      <b-form-input
                        id="campaignName"
                        v-model="form.name"
                        placeholder="e.g. Summer Racing Carnival 2025"
                        required
                      ></b-form-input>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>

                <!-- Internal Code -->
                <b-col md="6">
                  <b-form-group>
                    <p for="campaignName" class="mb-0 font-weight-bold">
                      Internal Reference Code
                    </p>
                    <b-form-input
                      id="referenceCode"
                      v-model="form.code"
                      placeholder="e.g. SRC-2025-06"
                    ></b-form-input>
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-row>
                    <b-col cols="6">
                      <validation-provider
                        #default="{ errors }"
                        name="start date"
                        rules="required"
                      >
                        <p class="font-weight-bold mb-0">Start Date & Time*</p>
                        <flat-pickr
                          id="startDate"
                          v-model="form.startDate"
                          class="form-control"
                          :config="configDate"
                          placeholder="mm/dd/yyyy"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-col>
                    <b-col cols="6">
                      <validation-provider
                        #default="{ errors }"
                        name="end date"
                        rules="required"
                      >
                        <p class="font-weight-bold mb-0">End Date & Time**</p>
                        <flat-pickr
                          id="endDate"
                          v-model="form.endDate"
                          class="form-control"
                          :config="configDate"
                          placeholder="mm/dd/yyyy"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-col>
                  </b-row>
                </b-col>

                <!-- Description -->
                <b-col cols="12" class="mt-1">
                  <b-form-group>
                    <p class="font-weight-bold mb-0">Campaign Description</p>
                    <b-form-textarea
                      id="description"
                      v-model="form.description"
                      placeholder="Promotional banners for the upcoming Summer Racing Carnival featuring special betting offers and promotions."
                      rows="3"
                    ></b-form-textarea>
                  </b-form-group>
                </b-col>

                <b-col cols="12">
                  <b-form-group>
                    <b-form-checkbox switch v-model="form.isLoginRequired">
                      Visible to Non-Logged-In Users
                    </b-form-checkbox>
                    <small class="text-muted">
                      Show this banner to visitors who are not logged in (e.g.,
                      for sign-up promotions)
                    </small>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-form>
          </b-card>
        </b-col>
      </b-row>

      <b-row class="mt-2">
        <b-col cols="12">
          <b-card>
            <h5 class="font-weight-bold">Banner Assets</h5>
            <b-row>
              <b-col md="3" sm="6" v-for="banner in banners" :key="banner.key">
                <b-form-group>
                  <p class="font-weight-bold mb-0">{{ banner.label }}</p>

                  <!-- File input -->
                  <b-form-file
                    v-model="banner.file"
                    accept="image/*"
                    @input="onFileChange(banner)"
                    placeholder="Choose File"
                    browse-text="Choose File"
                  ></b-form-file>

                  <small class="text-muted d-block mt-1">{{
                    banner.desc
                  }}</small>

                  <!-- Preview + Loading -->
                  <b-skeleton
                    v-if="banner.loading"
                    class="skeleton-box mx-auto rounded border"
                    style="width: 100%; min-height: 150px"
                  ></b-skeleton>

                  <img
                    v-else-if="
                      banner.previewUrl && form.bannerPosition === banner.key
                    "
                    :src="banner.previewUrl"
                    alt="preview"
                    class="img-fluid rounded border"
                    style="
                      width: 100%;
                      min-width: 100%;
                      min-height: 150px;
                      height: 150px;
                      object-fit: cover;
                    "
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Banner link -->
            <validation-provider
              #default="{ errors }"
              name="banner link"
              rules="required"
            >
              <b-form-group
                label="Banner Link URL"
                label-class="font-weight-bold"
              >
                <b-form-input
                  v-model="form.bannerLink"
                  placeholder="https://example.com/promotion"
                ></b-form-input>
                <div>
                  <small class="text-danger">{{ errors[0] }}</small>
                </div>
                <small class="text-muted"
                  >Where should users go when they click the banner?</small
                >
              </b-form-group>
            </validation-provider>

            <!-- Schedule -->
            <b-form-group
              label="Schedule Banner or Show Now"
              label-class="font-weight-bold"
            >
              <b-form-radio-group
                v-model="form.showType"
                :options="scheduleOptions"
              />
            </b-form-group>

            <!-- Desktop Preview -->
            <div class="d-flex align-items-center justify-content-between">
              <h6 class="font-weight-bold mb-0">Desktop Preview</h6>
              <div class="d-flex" style="gap: 8px">
                <b-button variant="outline-secondary">
                  <feather-icon icon="UserMinusIcon" class="mr-1" /> Logged Out
                </b-button>
                <b-button variant="outline-secondary">
                  <feather-icon icon="UserCheckIcon" class="mr-1" /> Logged In
                </b-button>
              </div>
            </div>
            <div class="mt-2">
              <DesktopUI
                :banners="banners"
                :bannerPosition="form.bannerPosition"
              />
            </div>
          </b-card>
        </b-col>
      </b-row>
    </validation-observer>

    <b-row class="mt-1">
      <b-col cols="12">
        <b-card>
          <div class="d-flex align-items-center justify-content-between">
            <h6 class="font-weight-bold mb-0">Mobile Preview</h6>
            <div class="d-flex" style="gap: 8px">
              <b-button variant="outline-secondary">
                <feather-icon icon="UserMinusIcon" class="mr-1" /> Logged Out
              </b-button>
              <b-button variant="outline-secondary">
                <feather-icon icon="UserCheckIcon" class="mr-1" /> Logged In
              </b-button>
            </div>
          </div>
          <div class="mt-1"><MobileUI :bannerCarousel="banners[0]" /></div>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <b-card>
          <h5 class="font-weight-bold">Placement & Targeting</h5>
          <b-row class="mt-1">
            <b-col md="4">
              <b-form-group
                label="Display Frequency"
                label-class="font-weight-bold"
              >
                <b-form-select
                  v-model="form.displayFrequency"
                  :options="frequencyOptions"
                ></b-form-select>
              </b-form-group>
            </b-col>

            <b-col md="4">
              <b-form-group
                label="Max Impressions per User"
                label-class="font-weight-bold"
              >
                <b-form-input
                  v-model="form.maxImpressions"
                  type="number"
                  min="1"
                ></b-form-input>
              </b-form-group>
            </b-col>

            <b-col md="4">
              <b-form-group
                label="Priority Level"
                label-class="font-weight-bold"
              >
                <b-form-select
                  v-model="form.priorityLevel"
                  :options="priorityOptions"
                ></b-form-select>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Device targeting -->
          <b-form-group
            label="Device Targeting"
            label-class="font-weight-bold"
            class="mt-1"
          >
            <b-form-checkbox-group
              v-model="form.deviceTargets"
              :options="deviceOptions"
            ></b-form-checkbox-group>
          </b-form-group>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <b-card>
          <b-row>
            <b-col
              cols="12"
              class="d-flex align-items-center justify-content-between mb-1"
              ><h5 class="font-weight-bolder text-body">
                Customer Eligibility
              </h5>
              <input
                ref="fileInput"
                type="file"
                accept=".csv"
                hidden
                @change="handleFileUpload"
              />
              <b-button
                size="sm"
                variant="outline-primary"
                @click="onClickInput"
                :disabled="form.allToView"
              >
                <feather-icon icon="UploadIcon" />

                CSV File Upload
              </b-button>
            </b-col>

            <b-col cols="12">
              <b-form-checkbox v-model="form.allToView" switch>
                <p class="font-weight-bold" style="font-size: 16px">
                  All to View
                </p>
              </b-form-checkbox>

              <!-- Description -->
              <p class="text-muted ml-4 mb-0">
                Everyone sees this banner – no customer profiles or segments
                required
              </p>
            </b-col>
            <b-col cols="12" class="mt-1">
              <div
                :class="{ 'disabled-section': form.allToView }"
                :style="{
                  opacity: form.allToView ? 0.5 : 1,
                  pointerEvents: form.allToView ? 'none' : 'auto',
                }"
              >
                <div
                  class="d-flex align-items-center mb-1 cursor-pointer"
                  style="border-bottom: 1px solid #dee2e6"
                >
                  <div
                    @click="activeTabCustomer = 'Customer Profiles'"
                    style="padding: 6px 16px"
                    :class="{
                      'active-tab': activeTabCustomer === 'Customer Profiles',
                    }"
                  >
                    Customer Profiles
                  </div>
                  <div
                    @click="activeTabCustomer = 'Customer Segments'"
                    style="padding: 6px 16px"
                    :class="{
                      'active-tab': activeTabCustomer === 'Customer Segments',
                    }"
                  >
                    Customer Segments
                  </div>
                </div>

                <b-row>
                  <b-col style="min-height: 100px">
                    <!-- Customer Profiles -->
                    <div
                      v-if="activeTabCustomer === 'Customer Profiles'"
                      class="grid-3"
                    >
                      <div
                        class="d-flex align-items-center cursor-pointer"
                        v-for="item in profiles"
                        :key="item"
                        style="
                          border: 1px solid #dee2e6;
                          border-radius: 5px;
                          gap: 10px;
                          padding: 8px 10px;
                        "
                        :style="{
                          background: selectedProfile.some((i) => i === item)
                            ? '#0D6EFD'
                            : '#f8f9fa',
                          color: selectedProfile.some((i) => i === item)
                            ? 'white'
                            : 'black',
                        }"
                        @click="handleSelectProfile(item)"
                      >
                        <div
                          class="d-flex justify-content-center align-items-center"
                          style="
                            width: 16px;
                            height: 16px;
                            border-radius: 2px;
                            border: 1px solid #dee2e6;
                          "
                          :style="{
                            background: selectedProfile.some((i) => i === item)
                              ? '#0D6EFD'
                              : 'white',
                          }"
                        >
                          <feather-icon icon="CheckIcon" style="color: white" />
                        </div>
                        {{ item }}
                      </div>
                    </div>

                    <!-- Customer Segments -->
                    <div v-else>
                      <draggable
                        :list="customerSegments"
                        :group="{ name: 'tag', pull: 'clone', put: false }"
                        @change="handleDrag"
                        class="d-flex flex-wrap w-100"
                        :sort="false"
                      >
                        <b-col
                          v-for="(tag, index) in customerSegments"
                          :key="index"
                          cols="6"
                          md="3"
                          style="padding: 6px"
                        >
                          <b-card
                            border-variant="secondary"
                            body-class="p-0"
                            class="mb-0"
                            style="padding: 8px; border-radius: 4px"
                            :data-item="JSON.stringify(tag)"
                            ><span>{{ tag.name }}</span></b-card
                          >
                        </b-col>
                      </draggable>

                      <b-row>
                        <b-col
                          cols="12"
                          class="mt-1"
                          style="margin-bottom: 5px"
                        >
                          <small class="text-muted text-sm"
                            >Drag items to the table on the right to set
                            recurring issuance</small
                          ></b-col
                        >
                        <b-col>
                          <draggable
                            :value="selectedSegment"
                            @change="handleDrag"
                            @add="handleDrop"
                            group="tag"
                            class="w-100"
                          >
                            <div
                              style="
                                border: 1px solid #dee2e6;
                                border-radius: 5px;
                              "
                            >
                              <p
                                style="
                                  background: #f8f9fa;
                                  font-weight: 800;
                                  border-radius: 5px 5px 0 0;
                                  padding: 8px 10px;
                                  border-bottom: 1px solid #dee2e6;
                                "
                              >
                                Segment
                              </p>

                              <div
                                v-if="selectedSegment.length === 0"
                                class="d-flex align-items-center justify-content-center"
                                style="min-height: 100px"
                              >
                                <div>
                                  Drag profiles or segments here to include in
                                  this promotion
                                </div>
                              </div>
                              <div v-else>
                                <div
                                  v-for="(item, ind) in selectedSegment"
                                  :key="ind"
                                  class="d-flex justify-content-between"
                                  style="
                                    border-bottom: 1px solid #dee2e6;
                                    padding: 6px 16px;
                                  "
                                >
                                  <div class="d-flex flex-column">
                                    <span style="font-weight: 800">{{
                                      item.name
                                    }}</span>
                                    <span
                                      style="font-size: 12px"
                                      v-if="item.selectedUsers"
                                      >{{ item.selectedUsers.length }} customers
                                      from CSV</span
                                    >
                                  </div>
                                  <b-button
                                    size="sm"
                                    variant="outline-danger"
                                    @click="deleteSegmentItem(item)"
                                    style="height: 29px !important"
                                  >
                                    Remove
                                  </b-button>
                                </div>
                              </div>
                            </div>
                          </draggable>
                        </b-col>
                      </b-row>
                    </div>
                  </b-col>
                </b-row>
              </div>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { formatDateSend } from "@/helpers/utils";
import useToastHandler from "@/services/toastHandler";
import {
  BButton,
  BButtonGroup,
  BCard,
  BCardBody,
  BCol,
  BForm,
  BFormCheckbox,
  BFormCheckboxGroup,
  BFormFile,
  BFormGroup,
  BFormInput,
  BFormRadio,
  BFormRadioGroup,
  BFormSelect,
  BFormTextarea,
  BRow,
  BSkeleton,
} from "bootstrap-vue";
import { parse } from "csv-parse";
import { format } from "date-fns";
import { v4 as uuidv4 } from "uuid";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import flatPickr from "vue-flatpickr-component";
import Spinner from "@core/components/Spinner";
import "vue-slider-component/theme/default.css";
import DesktopUI from "../components/DesktopUI.vue";
import MobileUI from "../components/MobileUI.vue";
import draggable from "vuedraggable";
import { mapState, mapActions } from "vuex";

export default {
  components: {
    BButton,
    BCard,
    BRow,
    BCol,
    BButtonGroup,
    BCardBody,
    BFormInput,
    flatPickr,
    ValidationProvider,
    ValidationObserver,
    BFormTextarea,
    BFormCheckbox,
    BFormRadioGroup,
    BFormRadio,
    BFormGroup,
    BFormCheckboxGroup,
    BForm,
    BFormFile,
    MobileUI,
    DesktopUI,
    BFormSelect,
    draggable,
    Spinner,
    BSkeleton,
  },
  data() {
    const scheduleOptions = [
      { text: "Show Now", value: "now" },
      { text: "Schedule Send on Activation", value: "scheduled" },
    ];
    const frequencyOptions = [
      { value: "always", text: "Always" },
      { value: "once_per_session", text: "Once per session" },
      { value: "once_per_day", text: "Once per day" },
    ];
    const priorityOptions = [
      { value: "low", text: "Low" },
      { value: "medium", text: "Medium" },
      { value: "high", text: "High" },
    ];
    const deviceOptions = [
      { value: "desktop", text: "Desktop" },
      { value: "mobile-web", text: "Mobile Web" },
      { value: "mobile-app", text: "Mobile App" },
      { value: "tablet", text: "Tablet" },
    ];
    return {
      form: {
        name: "",
        code: "",
        startDate: "",
        endDate: "",
        description: "",
        displayFrequency: frequencyOptions[0].value,
        maxImpressions: 5,
        allToView: false,
        isLoginRequired: true,
        bannerLink: "",
        showType: scheduleOptions[0].value,
        priorityLevel: priorityOptions[0].value,
        deviceTargets: deviceOptions.map((option) => option.value),
        platform: "all",
        bannerPosition: "",
      },
      scheduleOptions,
      frequencyOptions,
      priorityOptions,
      deviceOptions,
      currentResource: null,
      configDate: {
        enableTime: true,
        dateFormat: "Y-m-d H:i:ss",
        enableSeconds: true,
        static: true,
      },
      banners: [
        {
          key: "carousel",
          label: "Carousel Banner (Web & Mobile)",
          desc: "Inside website carousel - 864x240px",
          width: 1200,
          height: 400,
          resourceId: null,
          previewUrl: null,
          loading: false,
        },
        {
          key: "top",
          label: "Top Banner",
          desc: "Full-width top banner - 1920x150px",
          width: 1920,
          height: 150,
          resourceId: null,
          previewUrl: null,
          loading: false,
        },
        {
          key: "left",
          label: "Left Sidebar Banner",
          desc: "Left side promotional banner - 220x600px",
          width: 220,
          height: 600,
          resourceId: null,
          previewUrl: null,
          loading: false,
        },
        {
          key: "right",
          label: "Right Sidebar Banner",
          desc: "Right side promotional banner - 300x600px",
          width: 300,
          height: 600,
          resourceId: null,
          previewUrl: null,
          loading: false,
        },
      ],
      profiles: [],
      selectedProfile: [],
      activeTabCustomer: "Customer Profiles",
      selectedSegment: [],
      customerSegments: [],
      tempCustomerSegments: [],
      tempProfiles: [],
    };
  },
  watch: {},
  async created() {
    await Promise.all([this.fetchSegmentList(), this.fetchProfileList()]);
    await this.fetchBannerDetail();
  },
  computed: {
    ...mapState("marketing/banner", {
      loading: "loading",
    }),
  },
  methods: {
    ...mapActions({
      uploadBannerImage: "marketing/banner/uploadBannerImage",
      updateBanner: "marketing/banner/updateBanner",
      getBannerDetail: "marketing/banner/getBannerDetail",
      getSegmentsList: "segment/list/getSegmentsList",
      getProfileList: "user/list/getProfileList",
      addBannerTargets: "marketing/banner/addBannerTargets",
      deleteBannerTargets: "marketing/banner/deleteBannerTargets",
    }),
    handleSelectProfile(item) {
      if (this.selectedProfile.some((i) => i === item))
        return (this.selectedProfile = this.selectedProfile.filter(
          (i) => i !== item
        ));
      this.selectedProfile = [...this.selectedProfile, item];
    },
    handleDrag(event) {},
    handleDrop(event) {
      const draggedTag = event.clone?.innerText?.trim();
      if (
        !draggedTag ||
        this.selectedSegment.some((i) => i.name === draggedTag)
      )
        return;
      const item = this.customerSegments.find((i) => i.name === draggedTag);
      this.selectedSegment.splice(event.newIndex, 0, {
        ...item,
      });
    },
    async fetchSegmentList() {
      const data = await this.getSegmentsList({
        limit: 100,
      });
      this.customerSegments = data.data.map((i) => {
        return {
          name: i.name,
          id: i.id,
        };
      });
    },
    async fetchProfileList() {
      const data = await this.getProfileList();
      this.profiles = data.map((i) => i.profileTypeId);
    },
    deleteSegmentItem(item) {
      this.selectedSegment = this.selectedSegment.filter(
        (i) => i.id !== item.id
      );
    },
    handleSelectedPosition(item) {
      if (this.selectedPosition.some((i) => i === item))
        return (this.selectedPosition = this.selectedPosition.filter(
          (i) => i !== item
        ));
      return (this.selectedPosition = [...this.selectedPosition, item]);
    },

    convertListToIdsTarget(l) {
      return l.reduce((acc, item, index) => {
        const key = `ids[${index}]`;

        acc[key] = item.id;

        return acc;
      }, {});
    },
    onClickInput() {
      const fileInput = this.$refs.fileInput;
      fileInput.click();
    },
    _resetInputFile() {
      this.$refs.fileInput.value = null;
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        this.parseCSV(reader.result);
      };
      reader.readAsText(file);
      this._resetInputFile();
    },
    parseCSV(csvData) {
      parse(csvData, { delimiter: "," }, (err, data) => {
        if (err) {
          console.error(err);
          return;
        }
        data.shift();
        const newList = data.map((i) => i[0]);

        if (newList.length > 0) {
          this.selectedSegment = [
            ...this.selectedSegment,
            {
              name: "Untitled",
              id: uuidv4(),
              targetType: "UserList",
              selectedUsers: newList,
            },
          ];
          this.activeTabCustomer = "Customer Segments";
        }
      });
    },
    mapBannerForm(data) {
      this.form = {
        ...this.form,
        name: data.name,
        code: data.code,
        startDate: data.startDate,
        endDate: data.endDate,
        description: data.description,
        displayFrequency:
          data.displayFrequency || this.frequencyOptions[0].value,
        maxImpressions: data.maxImpressions,
        priorityLevel: data.priorityLevel,
        isLoginRequired: !data.isLoginRequired,
        bannerLink: data.bannerLink,
        showType: data.showType || this.scheduleOptions[0].value,
        bannerPosition: data.bannerPosition,
        deviceTargets: data.deviceTargets.map((i) => i.deviceTargetId),
      };
    },
    mapBannerResource(data) {
      if (!data.resources?.length) return;
      const index = this.banners.findIndex(
        (b) => b.key === data.bannerPosition
      );
      if (index === -1) return;
      const resource = data.resources[0];
      this.banners[index] = {
        ...this.banners[index],
        resourceId: resource?.resourceId || null,
        previewUrl: resource?.url || null,
      };
      this.currentResource = this.banners[index];
    },
    mapBannerTargets(bannerTargets = []) {
      if (!bannerTargets.length) return;
      const isAllToView = bannerTargets.some(
        (t) => t.targetId === "all-user-verified"
      );
      if (isAllToView) this.form.allToView = true;
      this.selectedProfile = [];
      this.tempProfiles = [];
      this.selectedSegment = [];
      this.tempCustomerSegments = [];

      const profileSet = new Set();
      const segmentSet = new Set();
      const segmentMap = Object.fromEntries(
        this.customerSegments.map((s) => [s.id, s])
      );

      bannerTargets.forEach((t) => {
        if (t.targetType === "Profile" && !profileSet.has(t.targetId)) {
          profileSet.add(t.targetId);
          this.selectedProfile.push(t.targetId);
          this.tempProfiles.push(t);
        }

        if (t.targetType === "CustomerSegment") {
          const seg = segmentMap[t.targetId];
          if (seg && !segmentSet.has(seg.id)) {
            segmentSet.add(seg.id);
            this.selectedSegment.push(seg);
            this.tempCustomerSegments.push(t);
          }
        }
      });
    },
    async fetchBannerDetail() {
      const bannerId = this.$route.params.id;
      if (!bannerId) return;
      const data = await this.getBannerDetail(bannerId);
      if (!data) return;
      this.mapBannerForm(data);
      this.mapBannerResource(data);
      this.mapBannerTargets(data.bannerTargets);
    },

    async handleUpdateBanner(isSaveDraft = false) {
      const bannerId = this.$route.params.id;
      if (!bannerId) return;
      const valid = await this.$refs.formUpdate.validate();
      if (!valid) return;
      const statuses = {
        active: 1,
        draft: 3,
      };
      const newResource = this.banners.find(
        (banner) => this.form.bannerPosition === banner.key && banner.resourceId
      );
      if (!newResource) return;
      const resources = this.currentResource
        ? [
            {
              oldResourceId: this.currentResource.resourceId,
              resourceId: newResource.resourceId,
            },
          ]
        : [
            {
              resourceId: newResource.resourceId,
            },
          ];
      const deleteTargets = this.formatDeleteTargets();
      const addTargets = this.formatTargets();
      const requests = [];
      requests.push(
        this.updateBanner({
          bannerId,
          data: {
            ...this.form,
            status: isSaveDraft ? statuses.draft : statuses.active,
            isLoginRequired: !this.form.isLoginRequired,
            startDate: formatDateSend(this.form.startDate),
            endDate: formatDateSend(this.form.endDate),
            resources,
          },
        })
      );

      if (deleteTargets) {
        requests.push(this.deleteBannerTargets(deleteTargets));
      }
      if (addTargets?.length) {
        requests.push(
          this.addBannerTargets({
            bannerId,
            targets: addTargets,
          })
        );
      }
      try {
        await Promise.all(requests);
        await this.fetchBannerDetail();
        this.showToastSuccess("Update banner successfully!");
      } catch (error) {
        this.showToastError("Update banner failed!");
      }
    },
    formatTargets() {
      let targets = [];
      const tempProfileIds = new Set(this.tempProfiles.map((p) => p.targetId));
      const tempSegmentIds = new Set(
        this.tempCustomerSegments.map((s) => s.targetId)
      );
      this.selectedProfile.forEach((id) => {
        if (!tempProfileIds.has(id)) {
          targets.push({ targetType: "Profile", targetId: id });
        }
      });
      this.selectedSegment.forEach((segment) => {
        if (!tempSegmentIds.has(segment.id)) {
          targets.push({
            targetType: "CustomerSegment",
            targetId: segment.id,
          });
        }
      });
      const verifiedTarget = this.selectedSegment.find(
        (t) => t.id === "all-user-verified"
      );
      if (!verifiedTarget && this.form.allToView) {
        targets.push({
          targetType: "CustomerSegment",
          targetId: "all-user-verified",
        });
      }

      return targets.length ? targets : null;
    },
    formatDeleteTargets() {
      const deleteTargets = [];

      const selectedProfileSet = new Set(this.selectedProfile);
      const selectedSegmentSet = new Set(this.selectedSegment.map((s) => s.id));
      this.tempProfiles.forEach((item) => {
        if (!selectedProfileSet.has(item.targetId)) {
          deleteTargets.push(item);
        }
      });

      this.tempCustomerSegments.forEach((item) => {
        if (!selectedSegmentSet.has(item.targetId)) {
          deleteTargets.push(item);
        }
      });
      const verifiedTarget = this.tempCustomerSegments.find(
        (t) => t.targetId === "all-user-verified"
      );
      if (verifiedTarget && !this.form.allToView) {
        deleteTargets.push({
          id: verifiedTarget.id,
        });
      }
      if (!deleteTargets.length) return null;
      if (deleteTargets.length === 1) return deleteTargets[0].id;
      return deleteTargets.map((i) => i.id).join(",");
    },
    async onFileChange(banner) {
      if (!banner.file) return;
      this.form.bannerPosition = banner.key;
      banner.loading = true;
      const result = await this.uploadBannerImage(banner);
      banner.previewUrl = result[0].url;
      banner.resourceId = result[0].resourceId;
      banner.loading = false;
    },
  },
  setup() {
    const { showToastSuccess, showToastError } = useToastHandler();

    return {
      showToastSuccess,
      showToastError,

      format,
      formatDateSend,
    };
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/vue-select.scss";
.disabled-section {
  transition: opacity 0.3s ease;
  user-select: none;
}
p {
  margin-bottom: 0;
}
.circle-cancel {
  position: absolute;
  top: -4px;
  right: -7px;
  border: 1px solid black;
  background: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.box {
  padding: 6px 10px;
  border: 1px solid #6c757d;
  border-left: none;
  cursor: pointer;
  &-active {
    background-color: #6c757d;
    color: white;
  }
  &:first-child {
    border-left: 1px solid #6c757d;
    border-radius: 4px 0 0 4px;
  }
  &:last-child {
    border-radius: 0 4px 4px 0;
  }
}
.custom-tab-title {
  background-color: white;
  color: black;
  font-weight: 500;
  &-active {
    background-color: #007bff;
    color: black;
    font-weight: 700;
  }
  &:last-child {
    border-right: none;
  }
}
</style>

<style lang="scss" scoped>
.grid-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.active-tab {
  color: #0d6efd;
  border: 1px solid #dee2e6;
  border-radius: 5px 5px 0 0;
  border-bottom: 1px solid #0d6efd;
}

.active-position {
  background: #0d6efd;
  color: white;
}
</style>
